/**
 * ProgenyEnvironmentContext - Manages Progeny API environment selection.
 *
 * Stores the selected environment (dev/uat/prod) in localStorage and provides
 * it to all Progeny API calls via the X-Progeny-Environment header.
 *
 * This approach ensures consistent environment targeting across multiple API pods,
 * as each request carries its target environment rather than relying on server-side state.
 */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";

/** Valid Progeny environment keys */
export type ProgenyEnvKey = "prod" | "uat" | "dev";

/** localStorage key for persisting environment selection */
export const PROGENY_ENV_STORAGE_KEY = "progeny_environment";

/** Default environment if none is set */
export const DEFAULT_PROGENY_ENV: ProgenyEnvKey = "prod";

interface ProgenyEnvironmentContextValue {
  /** Currently selected environment */
  environment: ProgenyEnvKey;
  /** Update the selected environment */
  setEnvironment: (env: ProgenyEnvKey) => void;
  /** Whether the environment is being switched */
  switching: boolean;
}

const ProgenyEnvironmentContext = createContext<ProgenyEnvironmentContextValue | undefined>(
  undefined
);

/**
 * Get the current Progeny environment from localStorage.
 * Can be called from outside React (e.g., in API interceptors).
 */
export function getStoredProgenyEnvironment(): ProgenyEnvKey {
  if (typeof window === "undefined") return DEFAULT_PROGENY_ENV;
  try {
    const stored = localStorage.getItem(PROGENY_ENV_STORAGE_KEY);
    if (stored === "prod" || stored === "uat" || stored === "dev") {
      return stored;
    }
  } catch {
    // localStorage not available
  }
  return DEFAULT_PROGENY_ENV;
}

/**
 * Set the Progeny environment in localStorage.
 * Can be called from outside React.
 */
export function setStoredProgenyEnvironment(env: ProgenyEnvKey): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(PROGENY_ENV_STORAGE_KEY, env);
    // Dispatch custom event so other components can react
    window.dispatchEvent(new CustomEvent("progeny-env-change", { detail: env }));
  } catch {
    // localStorage not available
  }
}

interface ProgenyEnvironmentProviderProps {
  children: ReactNode;
}

/**
 * Provider component that manages Progeny environment state.
 * Wraps components that need access to Progeny environment selection.
 */
export function ProgenyEnvironmentProvider({ children }: ProgenyEnvironmentProviderProps) {
  const [environment, setEnvironmentState] = useState<ProgenyEnvKey>(() =>
    getStoredProgenyEnvironment()
  );
  const [switching, setSwitching] = useState(false);

  // Sync state with localStorage changes (e.g., from other tabs)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === PROGENY_ENV_STORAGE_KEY && e.newValue) {
        const newEnv = e.newValue as ProgenyEnvKey;
        if (newEnv === "prod" || newEnv === "uat" || newEnv === "dev") {
          setEnvironmentState(newEnv);
        }
      }
    };

    const handleCustomEvent = (e: CustomEvent<ProgenyEnvKey>) => {
      setEnvironmentState(e.detail);
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("progeny-env-change", handleCustomEvent as EventListener);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("progeny-env-change", handleCustomEvent as EventListener);
    };
  }, []);

  const setEnvironment = useCallback((env: ProgenyEnvKey) => {
    setSwitching(true);
    setStoredProgenyEnvironment(env);
    setEnvironmentState(env);
    // Brief delay to allow UI feedback
    setTimeout(() => setSwitching(false), 100);
  }, []);

  const value: ProgenyEnvironmentContextValue = {
    environment,
    setEnvironment,
    switching,
  };

  return (
    <ProgenyEnvironmentContext.Provider value={value}>
      {children}
    </ProgenyEnvironmentContext.Provider>
  );
}

/**
 * Hook to access Progeny environment context.
 * Must be used within a ProgenyEnvironmentProvider.
 */
export function useProgenyEnvironment(): ProgenyEnvironmentContextValue {
  const context = useContext(ProgenyEnvironmentContext);
  if (context === undefined) {
    throw new Error("useProgenyEnvironment must be used within a ProgenyEnvironmentProvider");
  }
  return context;
}

/**
 * Hook to get Progeny environment without throwing if not in provider.
 * Returns stored value from localStorage as fallback.
 */
export function useProgenyEnvironmentSafe(): ProgenyEnvKey {
  const context = useContext(ProgenyEnvironmentContext);
  if (context === undefined) {
    return getStoredProgenyEnvironment();
  }
  return context.environment;
}
